import { useState, useEffect } from 'react'

export type FinancialSkeletonVariant = 'kpi-row' | 'table' | 'chart' | 'full-dashboard'

export interface FinancialSkeletonProps {
  variant: FinancialSkeletonVariant
  className?: string
}

/**
 * Custom hook to detect user's preference for reduced motion
 */
const useReducedMotion = (): boolean => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  })

  useEffect(() => {
    if (typeof window === 'undefined') return

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')

    const handleChange = (event: MediaQueryListEvent): void => {
      setPrefersReducedMotion(event.matches)
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  return prefersReducedMotion
}

/**
 * Shimmer bar component - base building block for skeleton variants
 */
function ShimmerBar({
  width = '100%',
  height = '1rem',
  className = '',
  animated = true,
}: {
  width?: string | number
  height?: string | number
  className?: string
  animated?: boolean
}): JSX.Element {
  const widthStyle = typeof width === 'number' ? `${width}px` : width
  const heightStyle = typeof height === 'number' ? `${height}px` : height

  return (
    <div
      className={`rounded-monumenta ${className}`}
      style={{
        width: widthStyle,
        height: heightStyle,
        background: animated
          ? 'linear-gradient(90deg, #24262B 0%, rgba(210, 250, 82, 0.05) 50%, #24262B 100%)'
          : '#24262B',
        backgroundSize: '200% 100%',
        animation: animated ? 'shimmer 1.5s infinite' : 'none',
      }}
      data-testid="shimmer-bar"
    />
  )
}

/**
 * KPI Row Skeleton - matches KPICard layout (4 cards in a flex row)
 */
function KPIRowSkeleton({ animated }: { animated: boolean }): JSX.Element {
  return (
    <div className="flex gap-4 flex-wrap" data-testid="kpi-row-skeleton">
      {[1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="flex flex-col p-4 rounded-monumenta min-w-[140px] bg-brand-dark-light border border-brand-dark-lighter shadow-monumenta"
          style={{ width: '150px', height: '80px' }}
        >
          <div className="flex items-center gap-2 mb-2">
            <ShimmerBar width={24} height={24} animated={animated} />
            <ShimmerBar width={60} height={12} animated={animated} />
          </div>
          <ShimmerBar width="80%" height={24} animated={animated} />
        </div>
      ))}
    </div>
  )
}

/**
 * Table Skeleton - matches GroupedTable structure (~6 rows)
 */
function TableSkeleton({ animated }: { animated: boolean }): JSX.Element {
  return (
    <div
      className="bg-brand-dark-light/50 rounded-monumenta border border-gray-800/50 overflow-hidden"
      data-testid="table-skeleton"
    >
      {/* Table Header */}
      <div className="bg-brand-dark border-b border-gray-800 px-6 py-4 flex gap-4">
        <ShimmerBar width="20%" height={14} animated={animated} />
        <ShimmerBar width="15%" height={14} animated={animated} />
        <ShimmerBar width="15%" height={14} animated={animated} />
        <ShimmerBar width="15%" height={14} animated={animated} />
        <ShimmerBar width="10%" height={14} animated={animated} />
      </div>
      {/* Table Rows */}
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div
          key={i}
          className="px-6 py-4 border-b border-gray-800/50 flex gap-4 items-center"
        >
          <ShimmerBar width="20%" height={16} animated={animated} />
          <ShimmerBar width="15%" height={16} animated={animated} />
          <ShimmerBar width="15%" height={16} animated={animated} />
          <ShimmerBar width="15%" height={16} animated={animated} />
          <ShimmerBar width="10%" height={16} animated={animated} />
        </div>
      ))}
    </div>
  )
}

/**
 * Chart Skeleton - matches TimeSeriesChart/DREChart container (~400px height)
 */
function ChartSkeleton({ animated }: { animated: boolean }): JSX.Element {
  return (
    <div
      className="bg-brand-dark-light/50 rounded-monumenta border border-gray-800/50 p-6"
      style={{ height: '400px' }}
      data-testid="chart-skeleton"
    >
      {/* Chart Title Area */}
      <div className="flex justify-between items-center mb-4">
        <ShimmerBar width={200} height={20} animated={animated} />
        <div className="flex gap-2">
          <ShimmerBar width={80} height={32} animated={animated} />
          <ShimmerBar width={80} height={32} animated={animated} />
        </div>
      </div>
      {/* Chart Area */}
      <div className="flex items-end gap-2 h-[280px] pt-4">
        {/* Y-axis labels */}
        <div className="flex flex-col justify-between h-full w-12">
          <ShimmerBar width={40} height={12} animated={animated} />
          <ShimmerBar width={35} height={12} animated={animated} />
          <ShimmerBar width={40} height={12} animated={animated} />
          <ShimmerBar width={30} height={12} animated={animated} />
        </div>
        {/* Bar placeholders */}
        <div className="flex-1 flex items-end gap-4 justify-around">
          {[65, 80, 45, 90, 55, 70, 60, 85, 50, 75, 40, 88].map((h, i) => (
            <ShimmerBar
              key={i}
              width={24}
              height={`${h}%`}
              animated={animated}
              className="rounded-t-sm"
            />
          ))}
        </div>
      </div>
      {/* X-axis labels */}
      <div className="flex justify-around mt-4 pl-12">
        {['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'].map(
          (_, i) => (
            <ShimmerBar key={i} width={24} height={12} animated={animated} />
          )
        )}
      </div>
    </div>
  )
}

/**
 * Full Dashboard Skeleton - composes KPI row + chart + table
 */
function FullDashboardSkeleton({ animated }: { animated: boolean }): JSX.Element {
  return (
    <div className="space-y-8" data-testid="full-dashboard-skeleton">
      <KPIRowSkeleton animated={animated} />
      <ChartSkeleton animated={animated} />
      <TableSkeleton animated={animated} />
    </div>
  )
}

/**
 * FinancialSkeleton Component
 *
 * Branded loading skeleton with four variants for financial dashboard components.
 * Uses Monumenta brand colors with lime-green shimmer effect.
 * Respects user's prefers-reduced-motion preference.
 */
export function FinancialSkeleton({
  variant,
  className = '',
}: FinancialSkeletonProps): JSX.Element {
  const prefersReducedMotion = useReducedMotion()
  const animated = !prefersReducedMotion

  return (
    <div
      className={className}
      role="status"
      aria-label="Carregando..."
      aria-busy="true"
    >
      {variant === 'kpi-row' && <KPIRowSkeleton animated={animated} />}
      {variant === 'table' && <TableSkeleton animated={animated} />}
      {variant === 'chart' && <ChartSkeleton animated={animated} />}
      {variant === 'full-dashboard' && <FullDashboardSkeleton animated={animated} />}
    </div>
  )
}

export default FinancialSkeleton
